/**
 * <cart-upsell-carousel>
 *
 * Pagineert de upsellkaarten in de cart drawer. Elke pagina bevat maximaal het
 * ingestelde aantal kaarten; de pijltjes schuiven de pagina's horizontaal
 * (volgende = van rechts naar links). Er is bewust geen eigen scrollgebied:
 * de kaarten scrollen mee met de rest van de winkelwagen.
 *
 * Bevat geen cart-logica - toevoegen gebeurt via de product-form-component van
 * het thema, zodat er geen extra of dubbele cart-events ontstaan.
 */
class CartUpsellCarousel extends HTMLElement {
  #index = 0;
  #pages = [];
  #track = null;
  #viewport = null;
  #prevButton = null;
  #nextButton = null;
  #controller = null;
  #resizeObserver = null;
  #mutationObserver = null;
  #frame = 0;

  connectedCallback() {
    this.#setup();

    this.#mutationObserver = new MutationObserver(() => this.#scheduleSetup());
    this.#mutationObserver.observe(this, { childList: true, subtree: true });
  }

  disconnectedCallback() {
    this.#controller?.abort();
    this.#resizeObserver?.disconnect();
    this.#mutationObserver?.disconnect();
    cancelAnimationFrame(this.#frame);
  }

  #scheduleSetup() {
    cancelAnimationFrame(this.#frame);
    this.#frame = requestAnimationFrame(() => this.#setup());
  }

  #setup() {
    this.#controller?.abort();
    this.#resizeObserver?.disconnect();

    this.#track = this.querySelector('[data-cc-upsell-track]');
    this.#viewport = this.querySelector('[data-cc-upsell-viewport]');
    this.#pages = Array.from(this.querySelectorAll('[data-cc-upsell-page]'));
    this.#prevButton = this.querySelector('[data-cc-upsell-prev]');
    this.#nextButton = this.querySelector('[data-cc-upsell-next]');

    if (!this.#track || this.#pages.length === 0) return;

    this.#controller = new AbortController();
    const { signal } = this.#controller;

    this.#prevButton?.addEventListener('click', () => this.#goTo(this.#index - 1), { signal });
    this.#nextButton?.addEventListener('click', () => this.#goTo(this.#index + 1), { signal });
    this.addEventListener('change', this.#handleVariantChange, { signal });

    if ('ResizeObserver' in window) {
      this.#resizeObserver = new ResizeObserver(() => this.#syncHeight());
      for (const page of this.#pages) this.#resizeObserver.observe(page);
    } else {
      window.addEventListener('resize', () => this.#syncHeight(), { signal });
    }

    this.#index = Math.min(this.#index, this.#pages.length - 1);
    this.#render();
  }

  /**
   * Zet de gekozen variant in het formulier en werkt de prijzen in de kaart bij.
   * Bij een ring staat de knop uit tot er een maat gekozen is; hier gaat hij aan.
   * @param {Event} event
   */
  #handleVariantChange = (event) => {
    const select = event.target;
    if (!(select instanceof HTMLSelectElement) || !select.hasAttribute('data-cc-variant-select')) return;

    const card = select.closest('.cc-upsell__card');
    if (!card) return;

    const input = card.querySelector('input[name="id"]');
    if (input instanceof HTMLInputElement) input.value = select.value;

    const addButton = card.querySelector('button[name="add"]');
    if (addButton instanceof HTMLButtonElement) addButton.disabled = !select.value;

    const option = select.selectedOptions[0];
    if (!option) return;

    const price = card.querySelector('[data-cc-upsell-price]');
    if (price && option.dataset.price) price.textContent = option.dataset.price;

    const compare = card.querySelector('[data-cc-upsell-compare]');
    if (compare instanceof HTMLElement) {
      const compareAt = option.dataset.compare;
      compare.textContent = compareAt ?? '';
      compare.hidden = !compareAt;
    }
  };

  /** @param {number} index */
  #goTo(index) {
    const clamped = Math.min(Math.max(index, 0), this.#pages.length - 1);
    if (clamped === this.#index) return;
    this.#index = clamped;
    this.#render();
  }

  #render() {
    this.#track.style.transform = `translateX(-${this.#index * 100}%)`;

    this.#pages.forEach((page, i) => {
      const hidden = i !== this.#index;
      page.setAttribute('aria-hidden', hidden ? 'true' : 'false');
      for (const control of page.querySelectorAll('a, button, select')) {
        if (hidden) {
          control.setAttribute('tabindex', '-1');
        } else {
          control.removeAttribute('tabindex');
        }
      }
    });

    if (this.#prevButton) this.#prevButton.disabled = this.#index === 0;
    if (this.#nextButton) this.#nextButton.disabled = this.#index >= this.#pages.length - 1;

    this.#syncHeight();
  }

  #syncHeight() {
    const active = this.#pages[this.#index];
    if (!active || !this.#viewport) return;
    const height = active.getBoundingClientRect().height;
    if (height > 0) this.#viewport.style.setProperty('--cc-upsell-height', `${Math.ceil(height)}px`);
  }
}

if (!customElements.get('cart-upsell-carousel')) {
  customElements.define('cart-upsell-carousel', CartUpsellCarousel);
}

/**
 * Van het add-on product (de garantie) mag er maar een per bestelling in de
 * winkelwagen staan. Shopify kan dat zelf niet afdwingen: een maximum-aantal per
 * bestelling bestaat alleen voor B2B-catalogi, niet voor een gewone webshop.
 *
 * Twee maatregelen, want er zijn twee manieren om er meer dan een te krijgen:
 *
 *  1. Snel achter elkaar op de schakelaar klikken. Tot de lade opnieuw gerenderd
 *     is staat die knop nog op "toevoegen", dus drie kliks gaven drie garanties.
 *     De eerste klik gaat door, elke volgende wordt in de capture-fase gestopt -
 *     synchroon, want een knop pas in de volgende frame uitschakelen is te laat.
 *
 *  2. Het product los toevoegen via zijn productpagina. Daar helpt alleen een
 *     correctie achteraf: staat het aantal boven 1, dan wordt het teruggezet.
 *     Dat loopt via `updateQuantity` van `cart-items-component`, hetzelfde pad als
 *     de plus/min-knoppen, dus met dezelfde cart-events en geen dubbele.
 *
 * De correctie wordt na een cart-update een paar keer geprobeerd: de Section
 * Rendering API vervangt de lade pas na ongeveer een seconde, en daarvoor staat
 * het oude aantal nog in de DOM.
 */
(() => {
  const SWITCH = '.cc-addon__switch';
  const BUSY_MS = 2500;
  const RETRY_DELAYS = [500, 1200, 2200];

  let busy = false;
  let busyTimer = 0;

  document.addEventListener(
    'click',
    (event) => {
      const target = event.target;
      const button = target instanceof Element ? target.closest(SWITCH) : null;
      if (!button) return;

      if (busy) {
        event.preventDefault();
        event.stopImmediatePropagation();
        return;
      }

      busy = true;
      clearTimeout(busyTimer);
      busyTimer = setTimeout(() => {
        busy = false;
      }, BUSY_MS);
    },
    true
  );

  let correcting = false;

  const enforceSingleAddon = () => {
    if (correcting) return;

    const addon = document.querySelector('[data-cc-addon]');
    if (!(addon instanceof HTMLElement)) return;

    const line = Number(addon.dataset.addonLine || 0);
    const quantity = Number(addon.dataset.addonQty || 0);
    if (line < 1 || quantity <= 1) return;

    const cartItems = document.querySelector('cart-items-component[data-drawer]');
    if (!cartItems || typeof cartItems.updateQuantity !== 'function') return;

    correcting = true;
    cartItems.updateQuantity({ line, quantity: 1, action: 'change' });
    setTimeout(() => {
      correcting = false;
    }, 2500);
  };

  const schedule = () => {
    for (const delay of RETRY_DELAYS) setTimeout(enforceSingleAddon, delay);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', schedule);
  } else {
    schedule();
  }

  document.addEventListener('shopify:cart:lines-update', schedule);
})();
